import { useEffect, useRef, useState } from "react";
import { guideTopics } from "@/mocks/comicHelp";

export default function GuideExplorer() {
  const [active, setActive] = useState(guideTopics[0].id);
  const panelRef = useRef<HTMLDivElement>(null);
  const topic = guideTopics.find((t) => t.id === active) || guideTopics[0];

  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (guideTopics.some((t) => t.id === id)) {
        setActive(id);
        window.setTimeout(() => {
          panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
      }
    };
    window.addEventListener("bibi-go-topic", handler);
    return () => window.removeEventListener("bibi-go-topic", handler);
  }, []);

  return (
    <section id="guide" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-900">
            <i className="ri-tools-line"></i>
            使用指南
          </span>
          <h2 className="mt-4 font-heading text-2xl text-foreground-950 md:text-3xl">
            六大常见问题，按需取用
          </h2>
          <p className="mt-3 text-base leading-relaxed text-foreground-700">
            无论是网站打不开、加载慢，还是换源、收藏、搜索、登录的问题，点击对应标签即可查看详细的分步解决方法。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="问题分类">
          {guideTopics.map((t) => {
            const isActive = t.id === active;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(t.id)}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-medium transition-colors md:px-5 ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 ring-1 ring-background-300 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
              >
                <i className={`${t.icon} text-base`}></i>
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          ref={panelRef}
          className="mt-6 scroll-mt-24 overflow-hidden rounded-2xl border border-background-200 bg-background-100/60"
        >
          <div key={topic.id} className="grid gap-0 lg:grid-cols-5">
            <div className="flex flex-col justify-center gap-3 border-b border-background-200 p-6 md:p-8 lg:col-span-2 lg:border-b-0 lg:border-r">
              <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500 text-background-50`}>
                <i className={`${topic.icon} text-2xl`}></i>
              </span>
              <h3 className="font-heading text-xl text-foreground-950 md:text-2xl">{topic.title}</h3>
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{topic.intro}</p>
            </div>

            <div className="p-6 md:p-8 lg:col-span-3">
              <ol className="space-y-4">
                {topic.steps.map((s, i) => (
                  <li key={s.title} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-400 font-heading text-sm text-foreground-950">
                      {i + 1}
                    </span>
                    <div>
                      <h4 className="text-base font-semibold text-foreground-950">{s.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-700">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}